import React, { Suspense, useEffect, useState } from 'react';
import './LandingPage.css';
import MarketingImage from './MarketingImage';
import LandingPageBelowFold from './LandingPageBelowFold';
import PublicSiteHeader from './PublicSiteHeader';

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
const CONSTRAINED_NETWORK_TYPES = ['slow-2g', '2g', '3g'];

const HERO_PROOF_ITEMS = [
  {
    title: 'Realistic interview flows',
    description: 'Practice with guided questions and follow-ups that feel closer to a live round.',
  },
  {
    title: 'Instant answer feedback',
    description: 'Spot weak structure, vague examples, and missed signals while the answer is still fresh.',
  },
  {
    title: 'Resume and ATS review',
    description: 'Strengthen your resume alongside your speaking practice instead of fixing prep in separate tools.',
  },
  {
    title: 'Progress you can revisit',
    description: 'Track improvement, review sessions, and keep your preparation moving in one workspace.',
  },
];

function shouldUseLiteLandingExperience() {
  if (typeof window === 'undefined') {
    return false;
  }

  const isMobileViewport = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  const connection = navigator.connection;
  const isConstrainedConnection = Boolean(connection?.saveData) ||
    CONSTRAINED_NETWORK_TYPES.includes(connection?.effectiveType);

  return isMobileViewport || isConstrainedConnection;
}

const LandingPage = ({ onGetStarted }) => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [useLiteLandingExperience, setUseLiteLandingExperience] = useState(() => (
    shouldUseLiteLandingExperience()
  ));

  useEffect(() => {
    const mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const connection = navigator.connection;
    const updateLandingMode = () => {
      setUseLiteLandingExperience(shouldUseLiteLandingExperience());
    };

    updateLandingMode();

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', updateLandingMode);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(updateLandingMode);
    }

    if (typeof connection?.addEventListener === 'function') {
      connection.addEventListener('change', updateLandingMode);
    }

    return () => {
      if (typeof mobileQuery.removeEventListener === 'function') {
        mobileQuery.removeEventListener('change', updateLandingMode);
      } else if (typeof mobileQuery.removeListener === 'function') {
        mobileQuery.removeListener(updateLandingMode);
      }

      if (typeof connection?.removeEventListener === 'function') {
        connection.removeEventListener('change', updateLandingMode);
      }
    };
  }, []);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (showVideoModal) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showVideoModal]);

  useEffect(() => {
    if (!showVideoModal) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowVideoModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showVideoModal]);

  const openVideoModal = () => {
    setShowVideoModal(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
  };

  return (
    <div className="landing-page">
      <a className="skip-link" href="#main-content">Skip to content</a>

      <PublicSiteHeader
        currentPage="home"
        onPrimaryAction={onGetStarted}
        tone="home"
      />

      {showVideoModal && (
        <div className="video-modal-overlay" role="presentation" onClick={closeVideoModal}>
          <div
            className="video-modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-video-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="video-modal-close"
              onClick={closeVideoModal}
              aria-label="Close video"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <div className="video-wrapper">
              <h2 id="demo-video-title" className="sr-only">TryInterview product demo</h2>
              <video
                controls
                autoPlay
                playsInline
                preload="none"
                className="demo-video"
                src={`${process.env.PUBLIC_URL}/demo.mp4`}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}

      <main id="main-content" className="landing-page__main">
        <section className="hero-section" id="home">
          <div className={`hero-content${useLiteLandingExperience ? ' hero-content--single' : ''}`}>
            <div className="hero-text-container">
              <div className="hero-eyebrow-row">
                <span className="hero-eyebrow">AI Interview Training Platform</span>
                <span className="hero-availability">Built for serious job seekers</span>
              </div>
              <h1 className="hero-title">
                Practice interviews like they matter and walk into the real one with{' '}
                <span className="gradient-text">sharper answers under pressure.</span>
              </h1>
              <p className="hero-description">
                TryInterview gives you realistic AI mock interviews, instant answer feedback, resume review,
                and structured progress tracking in one focused workspace built to help candidates sound more
                confident, more relevant, and more prepared.
              </p>
              <div className="hero-buttons">
                <button type="button" className="primary-btn" onClick={onGetStarted}>Start Free Trial</button>
                <button type="button" className="secondary-btn" onClick={openVideoModal}>See Product Demo</button>
              </div>
              <div className="trust-badge">
                <span className="badge-icon" aria-hidden="true">✓</span>
                <span>Designed to turn practice into stronger real-interview performance</span>
              </div>

              <div className="hero-proof-grid" aria-label="Platform strengths">
                {HERO_PROOF_ITEMS.map((item) => (
                  <article key={item.title} className="hero-proof-card">
                    <strong>{item.title}</strong>
                    <span>{item.description}</span>
                  </article>
                ))}
              </div>

              {useLiteLandingExperience && (
                <div className="hero-stats" aria-label="TryInterview highlights">
                  <div className="stat-item">
                    <span className="stat-number">50k+</span>
                    <span className="stat-label">Candidates</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">98%</span>
                    <span className="stat-label">Confidence boost</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">24/7</span>
                    <span className="stat-label">Practice access</span>
                  </div>
                </div>
              )}
            </div>

            {!useLiteLandingExperience && (
              <div className="hero-image-container">
                <MarketingImage
                  src="/practice-interview-online.png"
                  alt="Illustration of a live interview practice session on TryInterview"
                  className="hero-image"
                  priority
                  sizes="(max-width: 1100px) 44vw, 480px"
                />
              </div>
            )}
          </div>
        </section>
        <div className="below-fold-trigger" aria-hidden="true" />

        <Suspense fallback={null}>
          <LandingPageBelowFold onGetStarted={onGetStarted} />
        </Suspense>
      </main>
    </div>
  );
};

export default LandingPage;
